/**
 * Declared list of farm segment fields that are stored and replayed as one value, never diffed.
 *
 * Purpose: each value here is the output of one rating, not an edit to the previous segment's value,
 * so a partial delta of it is meaningless. `farmClarionDoorIds` is also stored in two shapes. Offer
 * rating and bind write the `{ liabilityClarionDoorId, risksClarionDoorIds }` map the schema
 * declares; endorsement re-rating (#35036) and renewals write the farm rater's positional, id-less
 * `{ locationId, ids }[]`. Diffing or merging across those shapes either threw
 * (`t.findIndex is not a function`, cfm-production 2026-09-22) or wrote keys of one onto the other.
 *
 * Flow: two sites have to agree. The policy-mod delta writer (`calculateSegmentDelta`) carries the
 * current value whole whenever it changes, and the farm-utils reader (`applyFarmObjectDelta`)
 * assigns it verbatim. Either one alone loses data: a whole read of a sparse delta drops the
 * unchanged entries.
 *
 * Key decision: separate from `array-merge-policy` because that registry is checked against the
 * schema's list fields, and the schema declares `farmClarionDoorIds` as an object.
 *
 * Limit: keyed by bare field name, like the other segment-merge registries.
 */
export const WHOLE_VALUE_FIELD_NAMES: readonly string[] = Object.freeze(['farmClarionDoorIds']);

export function isWholeValueField(fieldName: string): boolean {
  return WHOLE_VALUE_FIELD_NAMES.includes(fieldName);
}

/**
 * A segment's rating outputs: each is the complete result of rating that segment (every risk's coverage rows
 * and premium nodes), stored whole on every rated segment, never an edit to the previous segment's.
 * makePolicyDetailsFromSegments takes a later segment's value as is; merging it into the previous one kept
 * a removed risk's coverage rows and premium (#37449 review). A segment that was not rated carries none, or
 * an empty placeholder (isRatingSnapshot), and keeps the earlier snapshot.
 *
 * Separate from WHOLE_VALUE_FIELD_NAMES on purpose: those are matched by bare name at any depth and also
 * steer the policy-mod delta writer, while these apply only to the segment's own top-level fields (a
 * risk's `coverages` stays an ordinary delta) and only to the reader.
 */
export const SEGMENT_RATING_SNAPSHOT_FIELD_NAMES: readonly string[] = Object.freeze([
  'coverages',
  'premiumBreakdown',
  'segmentPremiumBreakdown'
]);

/**
 * Whether a segment's value of a rating output is a rating, i.e. a snapshot to take. A segment that is not
 * re-rated (a mortgagee-only change, say) is copied forward without its rating, and setSegmentValues leaves an
 * empty `premiumBreakdown: {}` on it: that empty value (or an absent / empty one) is a placeholder that keeps
 * the earlier snapshot, never a rating that replaces it (#37475).
 */
export function isRatingSnapshot(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return typeof value !== 'object' || Object.keys(value as object).length > 0;
}
